import { parseGwei } from 'viem';

// -------------------------
// Single source of truth for deposit gas settings.
// Used by useDeposit (what is actually sent), useTokenOneMax (MAX reserve)
// and TransferView (pre-send gas check, via useTokenOneMax re-exports).
// -------------------------

// depositETH uses ~62k gas on mainnet; 100k leaves ~60% headroom.
// Kept hardcoded (not estimated) on purpose: MetaMask mobile can suppress the
// confirmation sheet when eth_estimateGas fails or returns nothing.
export const ETH_DEPOSIT_GAS_LIMIT = 100_000n;

// Validator tip bounds. Left to MetaMask, the tip was 2 gwei on a 0.07 gwei
// base-fee network — ~97% of the total fee.
const MIN_PRIORITY_FEE_PER_GAS = parseGwei('0.05');
const MAX_PRIORITY_FEE_PER_GAS = parseGwei('0.5');

// maxFeePerGas = baseFee * 2 + tip. Survives ~6 consecutive full blocks of
// base-fee increases (12.5% each). Only baseFee + tip is actually paid; the
// rest is a cap, so this does not raise the real cost.
const BASE_FEE_MULTIPLIER = 2n;

function clampPriorityFee(value) {
  if (value < MIN_PRIORITY_FEE_PER_GAS) return MIN_PRIORITY_FEE_PER_GAS;
  if (value > MAX_PRIORITY_FEE_PER_GAS) return MAX_PRIORITY_FEE_PER_GAS;
  return value;
}

async function getSuggestedPriorityFee(publicClient) {
  try {
    return await publicClient.estimateMaxPriorityFeePerGas();
  } catch (error) {
    return MIN_PRIORITY_FEE_PER_GAS;
  }
}

export async function getDepositFees(publicClient) {
  const [block, suggestedTip] = await Promise.all([
    publicClient.getBlock({ blockTag: 'latest' }),
    getSuggestedPriorityFee(publicClient),
  ]);

  const baseFee = block?.baseFeePerGas;

  // Non-EIP-1559 chain fallback.
  if (baseFee === null || baseFee === undefined) {
    const gasPrice = await publicClient.getGasPrice();
    return { maxFeePerGas: gasPrice, maxPriorityFeePerGas: gasPrice };
  }

  const maxPriorityFeePerGas = clampPriorityFee(suggestedTip);
  const maxFeePerGas = baseFee * BASE_FEE_MULTIPLIER + maxPriorityFeePerGas;

  return { maxFeePerGas, maxPriorityFeePerGas };
}
